import { useState, useMemo, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Building2, CheckCircle2, ShieldAlert, AlertCircle } from 'lucide-react';
import { adminSites, adminUsers, auth, ApiError, setAuthToken } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { SingleSelectSearch } from '@/components/ui/SingleSelectSearch';
import { listTimeZones, browserTimeZone } from '@/lib/timezones';
import { toast } from 'sonner';
import { errorText } from '@/lib/permissionError';

/**
 * DREAM-8 — Site onboarding form (simplified).
 *
 * Just the site name and an optional first site admin pulled from existing
 * users. Region defaults server-side (us-east-1); tenant defaults to the
 * site_id. Platform admins already see every active site in their session,
 * so no extra membership wiring is needed for the operator.
 */

export function SiteOnboardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { switchSite } = useAuth();

  const [name, setName] = useState('');
  const [timeZone, setTimeZone] = useState(browserTimeZone());
  const [adminUserId, setAdminUserId] = useState('');
  const [createdSite, setCreatedSite] = useState<{ site_id: string; name: string } | null>(null);
  const [createError, setCreateError] = useState<{ type: 'forbidden' | 'conflict' | 'generic'; message: string } | null>(null);

  // Existing users to pick the first site admin from. Optional — operator can
  // skip and invite later from the Team page.
  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });

  // Surface users who already hold a site_admin role somewhere first — they're
  // the most likely candidates to admin another site. Everyone else follows.
  // Only active accounts are selectable — the backend rejects invited/locked/
  // disabled users here (they can't use the new access yet), so filtering
  // them out of the picker avoids that round-trip entirely.
  const userOptions = useMemo(() => {
    const rows = (usersQ.data?.rows ?? []).filter((u) => u.status === 'active' && !u.locked_at);
    const isSiteAdmin = (u: typeof rows[number]) =>
      u.sites.some((s) => s.role === 'site_admin' || s.role === 'platform_admin' || s.role === 'application_admin');
    const sorted = [...rows].sort((a, b) => {
      const sa = isSiteAdmin(a) ? 0 : 1;
      const sb = isSiteAdmin(b) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (a.full_name || a.email).localeCompare(b.full_name || b.email);
    });
    return sorted;
  }, [usersQ.data]);

  const selectedUser = useMemo(
    () => userOptions.find((u) => u.user_id === adminUserId),
    [userOptions, adminUserId],
  );

  const create = useMutation({
    mutationFn: () =>
      adminSites.create({
        name: name.trim(),
        time_zone: timeZone,
        admin_email: selectedUser?.email,
      }),
    onSuccess: async (data) => {
      toast.success(`Site "${name.trim()}" created`);
      setCreatedSite({ site_id: data.site_id, name: name.trim() });
      qc.invalidateQueries({ queryKey: ['admin-sites'] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });

      // Refresh JWT so allowed_sites includes the new site, then auto-switch.
      try {
        const refreshed = await auth.refresh();
        setAuthToken(refreshed.access_token);
      } catch {
        // Non-fatal — operator can switch manually if refresh blips.
      }
      try {
        await switchSite(data.site_id, name.trim());
        navigate('/dashboard');
      } catch (err) {
        const msg = err instanceof ApiError
          ? `Site created but auto-switch failed: ${err.message}. Use the site picker to switch manually.`
          : 'Site created but auto-switch failed. Use the site picker to switch manually.';
        toast.error(msg);
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 403) {
        setCreateError({
          type: 'forbidden',
          message: 'You don\'t have permission to create sites. Only Platform Admins can register new clinics.',
        });
      } else if (err instanceof ApiError && err.status === 409) {
        setCreateError({
          type: 'conflict',
          message: err.message,
        });
      } else {
        setCreateError({
          type: 'generic',
          message: errorText(err, 'Site creation failed. Please try again.'),
        });
        toast.error(errorText(err, 'Site creation failed'));
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setCreateError(null);
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin/sites"
        title="New site"
        subtitle="Registers the clinic on the platform. Platform admins get access automatically. Optionally promote an existing user to site admin in the same step."
      />
      <div className="mx-auto max-w-2xl space-y-6">

      {createError?.type === 'forbidden' && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <ShieldAlert className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Permission denied</p>
            <p className="mt-0.5 text-sm text-red-700">{createError.message}</p>
          </div>
        </div>
      )}

      {createError && createError.type !== 'forbidden' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
          <p className="text-sm text-amber-800">{createError.message}</p>
        </div>
      )}

      {createdSite ? (
        <div className="card space-y-4 p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-success-600" />
            <div>
              <h2 className="text-base font-semibold text-ink-primary">
                Site "{createdSite.name}" created
              </h2>
              <p className="mt-1 text-sm text-ink-muted">Switching you into the new site. Please wait, it can take few seconds...</p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="card space-y-5 p-6">
          <div>
            <label className="label text-xs" htmlFor="name">
              Site name *
            </label>
            <input
              id="name" type="text" required minLength={2} maxLength={120}
              className="input" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Memorial Research Center"
              disabled={create.isPending}
            />
            <p className="mt-1 text-xs text-ink-muted">
              Must be unique across the platform.
            </p>
          </div>

          <div>
            <label className="label text-xs" htmlFor="time_zone">
              Time zone *
            </label>
            <SingleSelectSearch
              options={listTimeZones().map((tz) => ({ value: tz, label: tz }))}
              value={timeZone}
              onChange={(next) => setTimeZone(next ?? '')}
              placeholder="Search time zones…"
              disabled={create.isPending}
            />
            <p className="mt-1 text-xs text-ink-muted">
              Timestamps for this site display in this zone. Data is stored in UTC.
            </p>
          </div>

          <div>
            <label className="label text-xs" htmlFor="admin">
              First site admin <span className="text-ink-muted">(optional)</span>
            </label>
            <select
              id="admin"
              className="input"
              value={adminUserId}
              onChange={(e) => setAdminUserId(e.target.value)}
              disabled={usersQ.isLoading || create.isPending}
            >
              <option value="">
                {usersQ.isLoading ? 'Loading users…' : 'Select'}
              </option>
              {userOptions.map((u) => {
                const label = u.full_name ? `${u.full_name} (${u.email})` : u.email;
                return (
                  <option key={u.user_id} value={u.user_id}>{label}</option>
                );
              })}
            </select>
            <p className="mt-1 text-xs text-ink-muted">
              {selectedUser
                ? `${selectedUser.email} will be granted Site Admin at this site immediately.`
                : 'Existing users who already admin another site appear at the top. You can also skip this and invite the first admin later.'}
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => navigate('/admin/sites')}>
              Cancel
            </button>
            <button
              type="submit" className="btn-primary"
              disabled={create.isPending || name.trim().length < 2}
            >
              {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
              Create site
            </button>
          </div>
        </form>
      )}
      </div>
    </div>
  );
}
